import type { GroupCouponBatch, LicensePaymentReceipt, LicensePgRefundSnapshot } from '../types/license';
import type { User } from '../types/alarm';
import { SERVICE_MERCHANT } from '../constants/merchantBusinessInfo';
import { formatPaymentMethodFromReceipt } from './portoneCardDisplay';

export interface LicenseProofRefundInfo {
  refundedAtDisplay: string;
  refundAmountKrw: number;
  paidAmountKrw: number;
  /** 부분 환불·수수료 등 보조 설명 */
  detailLines?: string[];
  /** PortOne PG 취소 내역 기준 */
  isPgSourced?: boolean;
}

export interface LicenseProofDisplayModel {
  orderId: string;
  orderName: string;
  buyerName: string;
  accountEmail: string;
  paidAtDisplay: string;
  cardType: string;
  cardNumberDisplay: string;
  approvalNumber: string;
  supplyAmount: number;
  taxFreeAmount: number;
  vatAmount: number;
  totalAmount: number;
  currency: string;
  /** PortOne에 저장된 스냅샷이 있는지 */
  hasPgSnapshot: boolean;
  merchant: typeof SERVICE_MERCHANT;
  /** 구매 시 Firestore에 같이 저장된 PortOne 공식 영수증 URL */
  storedPgReceiptUrl?: string | null;
  /** 환불 완료 건에만 포함 (PG 기준, 없으면 모달에서 PortOne 조회) */
  refund?: LicenseProofRefundInfo | null;
  /** 환불 건이면 true — PG 취소 내역 조회·표시 대상 */
  expectsRefundInfo?: boolean;
}

export function formatProofDateTime(iso: string | undefined, fallbackDate: Date | null): string {
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const h = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      const s = String(d.getSeconds()).padStart(2, '0');
      return `${y}-${m}-${day} ${h}:${min}:${s}`;
    }
  }
  if (fallbackDate && !Number.isNaN(fallbackDate.getTime())) {
    const y = fallbackDate.getFullYear();
    const m = String(fallbackDate.getMonth() + 1).padStart(2, '0');
    const day = String(fallbackDate.getDate()).padStart(2, '0');
    const h = String(fallbackDate.getHours()).padStart(2, '0');
    const min = String(fallbackDate.getMinutes()).padStart(2, '0');
    const s = String(fallbackDate.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${day} ${h}:${min}:${s}`;
  }
  return '-';
}

function planProductLabel(license: { planType?: string; duration?: number; planId?: string }): string {
  const pt = String(license.planType || '').toUpperCase();
  const d = typeof license.duration === 'number' ? license.duration : 1;
  const year = d === 12 || String(license.planId || '').endsWith('-12');
  return `${pt} ${year ? '1년' : `${d}개월`} 이용권`;
}

function coerceDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof (v as any).toDate === 'function') {
    try {
      return (v as any).toDate();
    } catch {
      return null;
    }
  }
  const d = new Date(v as any);
  return Number.isNaN(d.getTime()) ? null : d;
}

function refundInfoFromPgSnapshot(
  pg: LicensePgRefundSnapshot,
  paidAmountKrw: number,
  fallbackDate: Date | null
): LicenseProofRefundInfo {
  const detailLines: string[] = ['포트원(PG) 등록 취소 내역 기준'];
  if (pg.reason) detailLines.push(`취소 사유: ${pg.reason}`);
  return {
    refundedAtDisplay: formatProofDateTime(pg.refundedAt, fallbackDate),
    refundAmountKrw: pg.refundAmountKrw,
    paidAmountKrw,
    detailLines,
    isPgSourced: true,
  };
}

function resolveLicenseRefundInfo(license: Record<string, unknown>): LicenseProofRefundInfo | null {
  if (license.isRefunded !== true) return null;

  const paidAmountKrw = typeof license.amount === 'number' ? license.amount : 0;
  const pg = license.pgRefundSnapshot as LicensePgRefundSnapshot | undefined;
  if (pg?.source === 'portone') {
    return refundInfoFromPgSnapshot(pg, paidAmountKrw, coerceDate(license.refundedAt));
  }

  /** PG 스냅샷 없으면 모달에서 PortOne 조회로 채움 — 앱 자체 환불 계산식은 영수증에 표시하지 않음 */
  return null;
}

function resolveGroupBatchRefundInfo(batch: GroupCouponBatch): LicenseProofRefundInfo | null {
  const qty = Number(batch.quantity || 0);
  const refundedCount = Number(batch.refundedCount || 0);
  const isFullyRefunded = batch.status === 'REFUNDED' || (qty > 0 && refundedCount >= qty);
  const refundAmountKrw = Number(batch.totalRefundKrw ?? batch.refundedAmountKrw ?? batch.refundKrw ?? 0);

  if (!isFullyRefunded && refundAmountKrw <= 0) return null;

  const paidAmountKrw = Number(batch.totalAmountKrw || 0);
  if (batch.pgRefundSnapshot?.source === 'portone') {
    const base = refundInfoFromPgSnapshot(batch.pgRefundSnapshot, paidAmountKrw, coerceDate(batch.refundedAt));
    const detailLines = [...(base.detailLines ?? [])];
    if (!isFullyRefunded && refundedCount > 0) {
      detailLines.push(`환불된 쿠폰: ${refundedCount}개 / ${qty}개`);
    }
    return { ...base, detailLines };
  }

  return null;
}

function buildBuyerFields(pr: LicensePaymentReceipt | null, user: User | null): { buyerName: string; accountEmail: string } {
  /** 결제 스냅샷(PortOne)에 저장된 이름 우선 — 없을 때만 계정 표시명 */
  const nameFromReceipt = typeof pr?.buyerName === 'string' ? pr.buyerName.trim() : '';
  const buyerName = (nameFromReceipt || user?.name?.trim() || '-').trim() || '-';
  const accountEmail = (user?.email?.trim() || '-').trim() || '-';
  return { buyerName, accountEmail };
}

export function buildLicenseProofDisplayModel(license: Record<string, unknown>, user: User | null): LicenseProofDisplayModel {
  const pr = (license.paymentReceipt ?? null) as LicensePaymentReceipt | null;
  const paidFallback = coerceDate(license.paidAt ?? license.purchasedAt);
  const amount = typeof license.amount === 'number' ? license.amount : 0;
  const currency = String(license.currency || pr?.currency || 'KRW');

  const total = typeof pr?.totalAmount === 'number' ? pr.totalAmount : amount;
  const taxFree = typeof pr?.taxFreeAmount === 'number' ? pr.taxFreeAmount : 0;
  let supply = typeof pr?.supplyAmount === 'number' ? pr.supplyAmount : undefined;
  let vat = typeof pr?.vatAmount === 'number' ? pr.vatAmount : undefined;
  if (supply === undefined) {
    const taxable = Math.max(0, total - taxFree);
    supply = Math.round(taxable / 1.1);
    vat = taxable - supply;
  } else if (vat === undefined) {
    vat = Math.max(0, total - taxFree - supply);
  }

  const orderId = String(pr?.orderId || license.paymentId || '-');
  const orderName = String(pr?.orderName || planProductLabel(license as any)).trim() || planProductLabel(license as any);
  const { buyerName, accountEmail } = buildBuyerFields(pr, user);

  return {
    orderId,
    orderName,
    buyerName,
    accountEmail,
    paidAtDisplay: formatProofDateTime(pr?.paidAt, paidFallback),
    cardType: formatPaymentMethodFromReceipt(pr),
    cardNumberDisplay: String(pr?.cardNumberMasked || '-').trim() || '-',
    approvalNumber: String(pr?.approvalNumber || '-').trim() || '-',
    supplyAmount: supply ?? 0,
    taxFreeAmount: taxFree,
    vatAmount: vat ?? 0,
    totalAmount: total,
    currency,
    hasPgSnapshot: !!(
      pr &&
      (pr.orderName || pr.paymentMethodDisplay || pr.cardNumberMasked || pr.approvalNumber || pr.paidAt)
    ),
    merchant: SERVICE_MERCHANT,
    storedPgReceiptUrl:
      typeof pr?.receiptUrl === 'string' && /^https?:\/\//i.test(pr.receiptUrl) ? pr.receiptUrl.trim() : null,
    refund: resolveLicenseRefundInfo(license),
    expectsRefundInfo: license.isRefunded === true,
  };
}

function planTypeKo(pt: string): string {
  const m: Record<string, string> = { basic: '베이직', standard: '스탠다드', pro: '프로' };
  return m[pt] || pt;
}

function groupDefaultOrderName(batch: GroupCouponBatch): string {
  const p = planTypeKo(batch.planType);
  const term = batch.termMonths === 12 ? '1년' : `${batch.termMonths}개월`;
  return `[그룹] ${p} ${term} · ${batch.quantity}명`;
}

export function buildGroupBatchProofDisplayModel(batch: GroupCouponBatch, user: User | null): LicenseProofDisplayModel {
  const pr = (batch.paymentReceipt ?? null) as LicensePaymentReceipt | null;
  const paidFallback = coerceDate(batch.createdAt);
  const amount = Number(batch.totalAmountKrw || 0);
  const currency = String(pr?.currency || 'KRW');

  const total = typeof pr?.totalAmount === 'number' ? pr.totalAmount : amount;
  const taxFree = typeof pr?.taxFreeAmount === 'number' ? pr.taxFreeAmount : 0;
  let supply = typeof pr?.supplyAmount === 'number' ? pr.supplyAmount : undefined;
  let vat = typeof pr?.vatAmount === 'number' ? pr.vatAmount : undefined;
  if (supply === undefined) {
    const taxable = Math.max(0, total - taxFree);
    supply = Math.round(taxable / 1.1);
    vat = taxable - supply;
  } else if (vat === undefined) {
    vat = Math.max(0, total - taxFree - supply);
  }

  const orderId = String(pr?.orderId || batch.paymentId || batch.batchId || '-');
  const orderName = String(pr?.orderName || groupDefaultOrderName(batch)).trim() || groupDefaultOrderName(batch);
  const { buyerName, accountEmail } = buildBuyerFields(pr, user);

  return {
    orderId,
    orderName,
    buyerName,
    accountEmail,
    paidAtDisplay: formatProofDateTime(pr?.paidAt, paidFallback),
    cardType: formatPaymentMethodFromReceipt(pr),
    cardNumberDisplay: String(pr?.cardNumberMasked || '-').trim() || '-',
    approvalNumber: String(pr?.approvalNumber || '-').trim() || '-',
    supplyAmount: supply ?? 0,
    taxFreeAmount: taxFree,
    vatAmount: vat ?? 0,
    totalAmount: total,
    currency,
    hasPgSnapshot: !!(
      pr &&
      (pr.orderName || pr.paymentMethodDisplay || pr.cardNumberMasked || pr.approvalNumber || pr.paidAt)
    ),
    merchant: SERVICE_MERCHANT,
    storedPgReceiptUrl:
      typeof pr?.receiptUrl === 'string' && /^https?:\/\//i.test(pr.receiptUrl) ? pr.receiptUrl.trim() : null,
    refund: resolveGroupBatchRefundInfo(batch),
    expectsRefundInfo:
      batch.status === 'REFUNDED' ||
      Number(batch.refundedCount || 0) > 0 ||
      Number(batch.totalRefundKrw ?? batch.refundedAmountKrw ?? 0) > 0,
  };
}
